import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RTLWrapper from "../components/RTLWrapper";
import { useUser } from "@clerk/clerk-react";
import { getUserProgress, updateLessonProgress } from "../data/database";
import SaveButton from "../components/SaveButton";

const arabicWords = [
  // Greetings & Basic Phrases
  { word: "السلام عليكم", pronunciation: "As-salaamu alaykum", meaning: "Peace be upon you" },
  { word: "مرحباً", pronunciation: "Marhaban", meaning: "Hello" },
  { word: "كيف حالك؟", pronunciation: "Kayfa ḥaaluka/ḥaaluki", meaning: "How are you?" },
  { word: "نعم", pronunciation: "Naʿam", meaning: "Yes" },
  { word: "لا", pronunciation: "Laa", meaning: "No" },
  { word: "شكراً", pronunciation: "Shukran", meaning: "Thank you" },
  { word: "من فضلك", pronunciation: "Min faḍlik", meaning: "Please" },
  { word: "عفواً", pronunciation: "ʿAfwan", meaning: "You're welcome" },
  { word: "آسف", pronunciation: "Āsif", meaning: "Sorry" },
  { word: "مع السلامة", pronunciation: "Maʿa as-salāmah", meaning: "Goodbye" },
  
  // Family Members
  { word: "أمي", pronunciation: "Ummi", meaning: "My mother" },
  { word: "أبي", pronunciation: "Abi", meaning: "My father" },
  { word: "أخي", pronunciation: "Akhi", meaning: "My brother" },
  { word: "أختي", pronunciation: "Ukhti", meaning: "My sister" },
  { word: "جدّي", pronunciation: "Jaddi", meaning: "My grandfather" },
  { word: "جدتي", pronunciation: "Jaddati", meaning: "My grandmother" },
  { word: "عمي", pronunciation: "ʿAmmi", meaning: "My uncle" },
  { word: "خالي", pronunciation: "Khali", meaning: "My maternal uncle" },
  { word: "ابني", pronunciation: "Ibnī", meaning: "My son" },
  { word: "ابنتي", pronunciation: "Ibntī", meaning: "My daughter" },
  
  // Home & Objects
  { word: "بيت", pronunciation: "Bayt", meaning: "House" },
  { word: "كتاب", pronunciation: "Kitaab", meaning: "Book" },
  { word: "قلم", pronunciation: "Qalam", meaning: "Pen" },
  { word: "ورقة", pronunciation: "Waraqah", meaning: "Paper" },
  { word: "مكتب", pronunciation: "Maktab", meaning: "Desk" },
  { word: "كرسي", pronunciation: "Kursī", meaning: "Chair" },
  { word: "باب", pronunciation: "Bāb", meaning: "Door" },
  { word: "نافذة", pronunciation: "Nāfidhah", meaning: "Window" },
  { word: "مصباح", pronunciation: "Miṣbāḥ", meaning: "Lamp" },
  { word: "سرير", pronunciation: "Sarīr", meaning: "Bed" },
  { word: "طاولة", pronunciation: "Ṭāwilah", meaning: "Table" },
  { word: "مطبخ", pronunciation: "Maṭbakh", meaning: "Kitchen" },
  { word: "حمام", pronunciation: "Ḥammām", meaning: "Bathroom" },
  { word: "غرفة", pronunciation: "Ghurfah", meaning: "Room" },
  { word: "حديقة", pronunciation: "Ḥadīqah", meaning: "Garden" },
  
  // Animals
  { word: "قطة", pronunciation: "Qiṭṭa", meaning: "Cat" },
  { word: "كلب", pronunciation: "Kalb", meaning: "Dog" },
  { word: "حصان", pronunciation: "Ḥiṣān", meaning: "Horse" },
  { word: "بقرة", pronunciation: "Baqarah", meaning: "Cow" },
  { word: "خروف", pronunciation: "Kharūf", meaning: "Sheep" },
  { word: "دجاجة", pronunciation: "Dajājah", meaning: "Chicken" },
  { word: "أسد", pronunciation: "Asad", meaning: "Lion" },
  { word: "فيل", pronunciation: "Fīl", meaning: "Elephant" },
  { word: "نمر", pronunciation: "Nimr", meaning: "Tiger" },
  { word: "دب", pronunciation: "Dubb", meaning: "Bear" },
  { word: "أرنب", pronunciation: "Arnab", meaning: "Rabbit" },
  { word: "فأر", pronunciation: "Faʾr", meaning: "Mouse" },
  { word: "طائر", pronunciation: "Ṭāʾir", meaning: "Bird" },
  { word: "سمكة", pronunciation: "Samakah", meaning: "Fish" },
  { word: "ثعبان", pronunciation: "Thuʿbān", meaning: "Snake" },
  
  // Colors
  { word: "أحمر", pronunciation: "Aḥmar", meaning: "Red" },
  { word: "أزرق", pronunciation: "Azraq", meaning: "Blue" },
  { word: "أخضر", pronunciation: "Akhḍar", meaning: "Green" },
  { word: "أصفر", pronunciation: "Aṣfar", meaning: "Yellow" },
  { word: "أسود", pronunciation: "Aswad", meaning: "Black" },
  { word: "أبيض", pronunciation: "Abyaḍ", meaning: "White" },
  { word: "بني", pronunciation: "Bunnī", meaning: "Brown" },
  { word: "رمادي", pronunciation: "Ramādī", meaning: "Gray" },
  { word: "وردي", pronunciation: "Wardī", meaning: "Pink" },
  { word: "برتقالي", pronunciation: "Burtuqālī", meaning: "Orange" },
  { word: "بنفسجي", pronunciation: "Banafsajī", meaning: "Purple" },
  { word: "ذهبي", pronunciation: "Dhahabī", meaning: "Gold" },
  { word: "فضي", pronunciation: "Fiḍḍī", meaning: "Silver" },
  
  // Numbers
  { word: "واحد", pronunciation: "Waaḥid", meaning: "One" },
  { word: "اثنان", pronunciation: "Ithnaan", meaning: "Two" },
  { word: "ثلاثة", pronunciation: "Thalāthah", meaning: "Three" },
  { word: "أربعة", pronunciation: "Arbaʿah", meaning: "Four" },
  { word: "خمسة", pronunciation: "Khamsah", meaning: "Five" },
  { word: "ستة", pronunciation: "Sittah", meaning: "Six" },
  { word: "سبعة", pronunciation: "Sabʿah", meaning: "Seven" },
  { word: "ثمانية", pronunciation: "Thamāniyah", meaning: "Eight" },
  { word: "تسعة", pronunciation: "Tisʿah", meaning: "Nine" },
  { word: "عشرة", pronunciation: "ʿAsharah", meaning: "Ten" },
  
  // Food & Drinks
  { word: "خبز", pronunciation: "Khubz", meaning: "Bread" },
  { word: "لحم", pronunciation: "Laḥm", meaning: "Meat" },
  { word: "دجاج", pronunciation: "Dajāj", meaning: "Chicken" },
  { word: "سمك", pronunciation: "Samak", meaning: "Fish" },
  { word: "أرز", pronunciation: "Aruzz", meaning: "Rice" },
  { word: "خضار", pronunciation: "Khuḍār", meaning: "Vegetables" },
  { word: "فواكه", pronunciation: "Fawākih", meaning: "Fruits" },
  { word: "تفاح", pronunciation: "Tuffāḥ", meaning: "Apple" },
  { word: "موز", pronunciation: "Mawz", meaning: "Banana" },
  { word: "برتقال", pronunciation: "Burtuqāl", meaning: "Orange" },
  { word: "حليب", pronunciation: "Ḥalīb", meaning: "Milk" },
  { word: "ماء", pronunciation: "Māʾ", meaning: "Water" },
  { word: "شاي", pronunciation: "Shāy", meaning: "Tea" },
  { word: "قهوة", pronunciation: "Qahwah", meaning: "Coffee" },
  { word: "عسل", pronunciation: "ʿAsal", meaning: "Honey" },
  
  // Body Parts
  { word: "رأس", pronunciation: "Raʾs", meaning: "Head" },
  { word: "عين", pronunciation: "ʿAyn", meaning: "Eye" },
  { word: "أذن", pronunciation: "Udhun", meaning: "Ear" },
  { word: "أنف", pronunciation: "Anf", meaning: "Nose" },
  { word: "فم", pronunciation: "Fam", meaning: "Mouth" },
  { word: "يد", pronunciation: "Yad", meaning: "Hand" },
  { word: "رجل", pronunciation: "Rijl", meaning: "Foot" },
  { word: "قلب", pronunciation: "Qalb", meaning: "Heart" },
  { word: "ظهر", pronunciation: "Ẓahr", meaning: "Back" },
  { word: "بطن", pronunciation: "Baṭn", meaning: "Stomach" },
  
  // Clothing
  { word: "قميص", pronunciation: "Qamīṣ", meaning: "Shirt" },
  { word: "بنطلون", pronunciation: "Bantalūn", meaning: "Pants" },
  { word: "فستان", pronunciation: "Fustān", meaning: "Dress" },
  { word: "حذاء", pronunciation: "Ḥidhāʾ", meaning: "Shoe" },
  { word: "قبعة", pronunciation: "Qubʿah", meaning: "Hat" },
  { word: "جورب", pronunciation: "Jawrab", meaning: "Sock" },
  { word: "حزام", pronunciation: "Ḥizām", meaning: "Belt" },
  { word: "ساعة", pronunciation: "Sāʿah", meaning: "Watch" },
  
  // Weather & Nature
  { word: "شمس", pronunciation: "Shams", meaning: "Sun" },
  { word: "قمر", pronunciation: "Qamar", meaning: "Moon" },
  { word: "نجمة", pronunciation: "Najmah", meaning: "Star" },
  { word: "سماء", pronunciation: "Samāʾ", meaning: "Sky" },
  { word: "أرض", pronunciation: "Arḍ", meaning: "Earth" },
  { word: "بحر", pronunciation: "Baḥr", meaning: "Sea" },
  { word: "نهر", pronunciation: "Nahr", meaning: "River" },
  { word: "جبل", pronunciation: "Jabal", meaning: "Mountain" },
  { word: "شجرة", pronunciation: "Shajarah", meaning: "Tree" },
  { word: "زهرة", pronunciation: "Zahrah", meaning: "Flower" },
  { word: "مطر", pronunciation: "Maṭar", meaning: "Rain" },
  { word: "ثلج", pronunciation: "Thalj", meaning: "Snow" },
  { word: "ريح", pronunciation: "Rīḥ", meaning: "Wind" },
  { word: "سحاب", pronunciation: "Saḥāb", meaning: "Cloud" },
  
  // Time & Days
  { word: "يوم", pronunciation: "Yawm", meaning: "Day" },
  { word: "ليلة", pronunciation: "Laylah", meaning: "Night" },
  { word: "صباح", pronunciation: "Ṣabāḥ", meaning: "Morning" },
  { word: "مساء", pronunciation: "Masāʾ", meaning: "Evening" },
  { word: "ساعة", pronunciation: "Sāʿah", meaning: "Hour" },
  { word: "دقيقة", pronunciation: "Daqīqah", meaning: "Minute" },
  { word: "أسبوع", pronunciation: "Usbūʿ", meaning: "Week" },
  { word: "شهر", pronunciation: "Shahr", meaning: "Month" },
  { word: "سنة", pronunciation: "Sanah", meaning: "Year" },
  
  // Transportation
  { word: "سيارة", pronunciation: "Sayyārah", meaning: "Car" },
  { word: "حافلة", pronunciation: "Ḥāfilah", meaning: "Bus" },
  { word: "قطار", pronunciation: "Qiṭār", meaning: "Train" },
  { word: "طائرة", pronunciation: "Ṭāʾirah", meaning: "Airplane" },
  { word: "دراجة", pronunciation: "Darrājah", meaning: "Bicycle" },
  { word: "قارب", pronunciation: "Qārib", meaning: "Boat" },
  { word: "مطار", pronunciation: "Maṭār", meaning: "Airport" },
  { word: "محطة", pronunciation: "Maḥaṭṭah", meaning: "Station" },
  
  // School & Learning
  { word: "مدرسة", pronunciation: "Madrasah", meaning: "School" },
  { word: "معلم", pronunciation: "Muʿallim", meaning: "Teacher" },
  { word: "طالب", pronunciation: "Ṭālib", meaning: "Student" },
  { word: "صف", pronunciation: "Ṣaff", meaning: "Class" },
  { word: "امتحان", pronunciation: "Imtiḥān", meaning: "Exam" },
  { word: "واجب", pronunciation: "Wājib", meaning: "Homework" },
  { word: "مكتبة", pronunciation: "Maktabah", meaning: "Library" },
  { word: "سبورة", pronunciation: "Sabbūrah", meaning: "Blackboard" },
  
  // Emotions & Feelings
  { word: "سعيد", pronunciation: "Saʿīd", meaning: "Happy" },
  { word: "حزين", pronunciation: "Ḥazīn", meaning: "Sad" },
  { word: "غاضب", pronunciation: "Ghāḍib", meaning: "Angry" },
  { word: "خائف", pronunciation: "Khāʾif", meaning: "Afraid" },
  { word: "متفائل", pronunciation: "Mutafaʾil", meaning: "Optimistic" },
  { word: "متعب", pronunciation: "Mutaʿab", meaning: "Tired" },
  { word: "جائع", pronunciation: "Jāʾiʿ", meaning: "Hungry" },
  { word: "عطشان", pronunciation: "ʿAṭshān", meaning: "Thirsty" },
  { word: "مرض", pronunciation: "Mariḍ", meaning: "Sick" },
  { word: "صحيح", pronunciation: "Ṣaḥīḥ", meaning: "Healthy" }
];

function WordLesson() {
  const { user } = useUser();
  const [selectedWord, setSelectedWord] = useState(null);
  const [clickedWords, setClickedWords] = useState(new Set());

  // Load progress from database on mount
  useEffect(() => {
    const loadProgress = () => {
      if (user) {
        const userProgress = getUserProgress(user.id);
        const learnedCount = userProgress.words || 0;
        if (learnedCount > 0) {
          setClickedWords(new Set(arabicWords.slice(0, learnedCount).map(w => w.word)));
        }
      }
    };
    loadProgress();
  }, [user]);

  const handleClick = async (wordObj) => {
    setSelectedWord(wordObj);
    const updated = new Set(clickedWords).add(wordObj.word);
    setClickedWords(updated);
    
    // Save progress to database
    if (user) {
      try {
        await updateLessonProgress(user.id, 'words', updated.size);
        console.log('Progress saved to database:', updated.size);
      } catch (error) {
        console.error('Error saving progress to database:', error);
      }
    }
    window.responsiveVoice.speak(wordObj.word, "Arabic Male");
  };

  const progress = (clickedWords.size / arabicWords.length) * 100;

  return (
    <RTLWrapper>
      <div className="p-4 sm:p-6 text-center h-full bg-gradient-to-br from-yellow-50 to-white overflow-y-auto">
        <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-yellow-800">كلمات عربية للمبتدئين</h2>

        {/* Progress Bar */}
        <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
          <motion.div
            className="bg-green-500 h-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <p className="text-sm text-gray-600 mb-6">تم تعلم {clickedWords.size} من {arabicWords.length} كلمة</p>

        <AnimatePresence>
          {selectedWord && (
            <motion.div
              key="word-info"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
              className="text-lg sm:text-xl text-green-700 mb-6"
            >
              <p>النطق: <strong>{selectedWord.pronunciation}</strong></p>
              <p>المعنى: <strong>{selectedWord.meaning}</strong></p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center mb-6">
          {arabicWords.map((wordObj, index) => {
            const learned = clickedWords.has(wordObj.word);
            return (
              <button
                key={index}
                onClick={() => handleClick(wordObj)}
                className={`text-xl font-semibold p-4 rounded-2xl shadow-md transition duration-200 text-wrap whitespace-pre-wrap break-words ${
                  learned ? "bg-green-100 text-green-700" : "bg-white text-yellow-800 hover:bg-yellow-100"
                }`}
              >
                {wordObj.word}
              </button>
            );
          })}
        </div>

        {/* Save Button */}
        <div className="flex justify-center mb-6">
          <SaveButton
            lessonType="words"
            progressData={clickedWords.size}
          />
        </div>

        {/* ResponsiveVoice Script */}
        <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
      </div>
    </RTLWrapper>
  );
}

export default WordLesson;
